<template>
  <div>
    <div class="text-right">
      <a-tag closable color="purple" v-if="infoVisible" class="mb-3" @close="infoVisible = false">
        <span class="py-2 pr-3">Attendance not yet submitted for: {{ selectedDate }}</span>
      </a-tag>
    </div>

    <a-spin :spinning="loading">
      <div class="py-5 text-center" v-if="!serverdata || !serverdata.attendancedata || !serverdata.attendancedata.length">
        <a-tag color="pink">No staff attendance data available</a-tag>
      </div>

      <div v-else class="table-responsive staff-attendance-table">
        <div class="row mb-2" v-if="serverdata && serverdata.attendancedata && serverdata.attendancedata.length">
          <div class="col-md-8">
            <button class="btn btn-sm btn-primary btn-with-addon mr-2 mb-2" @click="markAllAbsent">
              <span class="btn-addon">
                <i class="btn-addon-icon fe fe-x" />
              </span>
              Mark all Absent
            </button>

            <button class="btn btn-sm btn-primary btn-with-addon mr-2 mb-2" @click="markAllPresent">
              Mark all Present
              <span class="btn-addon">
                <i class="btn-addon-icon fe fe-check" />
              </span>
            </button>
            <button class="btn btn-sm btn-primary btn-with-addon mr-2 mb-2" @click="resetAll">
              <span class="btn-addon">
                <i class="btn-addon-icon fe fe-refresh-cw" /> </span>
              Reset
            </button>
            <a-input-search
              v-model="searchQuery"
              placeholder="Search by staff name or code"
              allow-clear
              style="width: 320px"
              class="mb-2"
            />
          </div>
          <div class="col-md-4 text-right text-md-right">
            <button class="btn btn-sm btn-with-addon mb-2 btn-success"
                    @click="handleSubmitAttendance"
                    :loading="loading"
                    :disabled="isHolidayOrSunday">
              <span class="btn-addon">
                <i class="btn-addon-icon fe fe-check" />
              </span>
              Update Attendance
            </button>
          </div>
        </div>
        <table class="table table-bordered table-sm table-striped table-hover bg-white attendance-compact-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Staff Code</th>
              <th>Staff Name</th>
              <th v-if="hasCumulativeLeaves" class="text-center cumulative-col-header" title="Cumulative leaves taken up to this month">
                Cum. Leaves
              </th>
              <th
                v-for="(daydata, index) in serverdata.headers"
                :key="index"
                class="date-col-header"
                :class="{'neo-bg-light-blue': daydata.date === selectedDate}"
              >
                {{ formatNumber(daydata.dayNo) }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(staff, staffIndex) in filteredStaff" :key="staff.id">
              <th>{{ staffIndex + 1 }}</th>
               <td>{{ staff.staffcode }}</td>
               <td class="staff-name-cell">
                <div class="d-flex align-items-center justify-content-between w-100">
                  <span class="text-truncate flex-grow-1" :title="staff.staffname">
                    {{ staff.staffname }}
                  </span>
                  <a-button
                    @click="openEditModal(staff)"
                    size="small"
                    type="link"
                    class="p-0"
                  >
                    <i class="fe fe-edit" />
                  </a-button>
                </div>
              </td>
              <td v-if="hasCumulativeLeaves" class="cumulative-col-cell text-center align-middle">
                <a-popover v-if="staff.cumulative_leaves" placement="right" trigger="hover">
                  <template slot="title">
                    <div class="font-weight-bold">
                      <i class="fe fe-calendar mr-1"></i> Cumulative Leaves (Session to {{ moment(selectedDate).format('MMM YYYY') }})
                    </div>
                  </template>
                  <template slot="content">
                    <div style="min-width: 220px; font-size: 12px;">
                      <div class="mb-1 text-muted" style="font-size: 11px;">
                        Rule: Leave/Sick/Absent = 1.0 | Half Day = 0.5 | 3 Late = 1
                      </div>
                      <div v-for="item in (staff.cumulative_leaves.items || [])" :key="item.key" class="d-flex justify-content-between py-1 border-bottom">
                        <span>{{ item.label }} <small class="text-muted">({{ item.sub_text }})</small>:</span>
                        <span class="font-weight-bold">{{ item.leaves }}</span>
                      </div>
                      <div class="d-flex justify-content-between pt-2 text-danger font-weight-bold" style="font-size: 13px;">
                        <span>Total Leaves Taken:</span>
                        <span>{{ staff.cumulative_leaves.total_leaves }}</span>
                      </div>
                    </div>
                  </template>
                  <a-tag
                    :color="staff.cumulative_leaves.total_leaves > 0 ? (staff.cumulative_leaves.total_leaves >= 10 ? 'red' : 'orange') : 'green'"
                    class="m-0 font-weight-bold"
                    style="cursor: pointer; font-size: 12px; padding: 1px 8px; line-height: 20px;"
                  >
                    {{ staff.cumulative_leaves.total_leaves }}
                  </a-tag>
                </a-popover>
                <span v-else class="text-muted">-</span>
              </td>
              <td
                v-for="attnd in staff.data"
                :key="attnd.date"
                :class="getCellClass(attnd.date)"
                @click="handleAttendanceClick(staff.id, attnd)"
              >
                <p class="text-center m-0">
                  <template v-if="isWeekendOrHoliday(attnd.date)">
                    <a-tag class="m-0 p-0 neoCellTagDisabled">
                      <i class="btn-addon-icon fe fe-hash" />
                    </a-tag>
                  </template>
                  <template v-else>
                    <a-tag
                      :color="getAttendanceColor(staff.id, attnd)"
                      class="m-0 p-0 neoCellTag"
                      :class="{'transparent-tag': getAttendanceColor(staff.id, attnd) === 'white' && attnd.date !== selectedDate}"
                    >
                      <template v-if="attnd.date === selectedDate">
                        <i v-if="presentStaffIds.includes(staff.id)" class="btn-addon-icon fe fe-check" />
                        <i v-else-if="absentStaffIds.includes(staff.id)" class="btn-addon-icon fe fe-x" />
                        <small v-else-if="lateStaffIds.includes(staff.id)" class="font-weight-bold">LT</small>
                        <small v-else-if="leaveStaffIds.includes(staff.id)" class="font-weight-bold">L</small>
                        <small v-else-if="halfDayStaffIds.includes(staff.id)" class="font-weight-bold">HD</small>
                        <small v-else-if="substituteStaffIds.includes(staff.id)" class="font-weight-bold">S</small>
                        <i v-else class="btn-addon-icon fe fe-square" />
                      </template>
                      <template v-else>
                         <i v-if="['P','HP','LO','OT','EL','LE'].includes(attnd.status)" class="btn-addon-icon fe fe-check" />
                        <i v-else-if="attnd.status === 'AB'" class="btn-addon-icon fe fe-x" />
                        <span v-else-if="attnd.status !== '-'">{{ attnd.status }}</span>
                      </template>
                    </a-tag>
                  </template>
                </p>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="row mt-2" v-if="serverdata && serverdata.attendancedata && serverdata.attendancedata.length">
        <div class="col-12">
          <div class="border-top mb-2"></div>
        </div>
        <div class="col-md-8">
          <button class="btn btn-sm btn-primary btn-with-addon mr-2 mb-2" @click="markAllAbsent">
            <span class="btn-addon">
              <i class="btn-addon-icon fe fe-x" />
            </span>
            Mark all Absent
          </button>

          <button class="btn btn-sm btn-primary btn-with-addon mr-2 mb-2" @click="markAllPresent">
            Mark all Present
            <span class="btn-addon">
              <i class="btn-addon-icon fe fe-check" />
            </span>
          </button>
          <button class="btn btn-sm btn-primary btn-with-addon mr-2 mb-2" @click="resetAll">
            <span class="btn-addon">
              <i class="btn-addon-icon fe fe-refresh-cw" /> </span>
            Reset
          </button>
        </div>
        <div class="col-md-4 text-right text-md-right">
          <button class="btn btn-sm btn-with-addon mb-2 btn-success"
                  @click="handleSubmitAttendance"
                  :loading="loading"
                  :disabled="isHolidayOrSunday">
            <span class="btn-addon">
              <i class="btn-addon-icon fe fe-check" />
            </span>
            Update Attendance
          </button>
        </div>
      </div>
    </a-spin>

    <a-modal
      :visible="editModalVisible"
      title="Edit Attendance"
      @cancel="closeEditModal"
      @ok="handleEditSubmit"
      :confirm-loading="editLoading"
    >
      <a-form layout="vertical">
        <div class="row mb-3">
          <div class="col">
            <strong>Staff Name:</strong> {{ selectedStaff.staffname }}
          </div>
          <div class="col">
            <strong>Staff Code:</strong> {{ selectedStaff.staffcode }}
          </div>
        </div>
        <div class="row mb-3">
          <div class="col">
            <strong>Date:</strong> {{ selectedDate }}
          </div>
        </div>
        <div class="row mb-3" v-if="selectedStaff && selectedStaff.cumulative_leaves">
          <div class="col">
            <a-alert
              type="info"
              show-icon
              style="padding: 6px 12px;"
            >
              <template slot="message">
                <span>
                  <strong>Cumulative Leaves (Session):</strong>
                  <span class="text-danger font-weight-bold ml-1">{{ selectedStaff.cumulative_leaves.total_leaves }}</span>
                  <small class="text-muted ml-2">
                    (Leaves/Sick/Absent: {{ selectedStaff.cumulative_leaves.leaves_count }}, Half-Days: {{ selectedStaff.cumulative_leaves.half_days_count }}, Late: {{ selectedStaff.cumulative_leaves.lates_count }})
                  </small>
                </span>
              </template>
            </a-alert>
          </div>
        </div>

        <a-form-item label="Status">
          <a-select v-model="editForm.status">
            <a-select-option value="P">Present</a-select-option>
            <a-select-option value="AB">Absent</a-select-option>
            <a-select-option value="LT">Late</a-select-option>
            <a-select-option value="L">Leave</a-select-option>
            <a-select-option value="HD">Half Day</a-select-option>
            <a-select-option value="S">Substitute</a-select-option>
          </a-select>
        </a-form-item>

        <div class="row" v-if="['P', 'LT', 'HD'].includes(editForm.status)">
          <div class="col-md-6">
            <a-form-item label="Start Time">
              <a-time-picker
                v-model="editForm.startTime"
                format="HH:mm"
                placeholder="Select start time"
                style="width: 100%"
              />
            </a-form-item>
          </div>
          <div class="col-md-6">
            <a-form-item label="End Time">
              <a-time-picker
                v-model="editForm.endTime"
                format="HH:mm"
                placeholder="Select end time"
                style="width: 100%"
              />
            </a-form-item>
          </div>
        </div>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import moment from 'moment'

export default {
  name: 'updateAttendance',
  props: {
    serverdata: { type: Object, required: true },
    selectedDate: { type: String, required: true },
  },
  data() {
    return {
      presentStaffIds: [],
      absentStaffIds: [],
      lateStaffIds: [],
      leaveStaffIds: [],
      halfDayStaffIds: [],
      substituteStaffIds: [],
      infoVisible: true,
      loading: false,
      editModalVisible: false,
      editLoading: false,
      selectedStaff: {
        staffname: '',
        staffcode: '',
        id: null,
        data: [],
      },
      editForm: {
        status: null,
        startTime: null,
        endTime: null,
      },
      searchQuery: '',
    }
  },
  computed: {
    isHolidayOrSunday() {
      if (!this.serverdata || !this.serverdata.headers) return false
      const dateInfo = this.serverdata.headers.find(d => d.date === this.selectedDate)
      return dateInfo && (dateInfo.day === 'Sun' || dateInfo.is_holiday)
    },
    hasCumulativeLeaves() {
      if (this.serverdata && this.serverdata.is_lfs) return true
      return !!(
        this.serverdata &&
        this.serverdata.attendancedata &&
        this.serverdata.attendancedata.some(s => s.cumulative_leaves)
      )
    },
    filteredStaff() {
      if (!this.serverdata || !this.serverdata.attendancedata) return []
      if (!this.searchQuery) return this.serverdata.attendancedata
      const q = this.searchQuery.toLowerCase()
      return this.serverdata.attendancedata.filter(staff => {
        return (
          (staff.staffname && staff.staffname.toLowerCase().includes(q)) ||
          (staff.staffcode && staff.staffcode.toLowerCase().includes(q))
        )
      })
    },
  },
  watch: {
    selectedDate: {
      handler: 'initializeAttendanceState',
      immediate: true,
    },
    serverdata: {
      handler: 'initializeAttendanceState',
      deep: true,
    },
  },
  methods: {
    moment,
    formatNumber(val) {
      return val < 10 ? '0' + val : val
    },
    getStatusCode(status) {
      switch (status) {
        case 'AB': return 0 // Absent
        case 'P': return 1 // Present
        case 'LT': return 2 // Late
        case 'L': return 3 // Leave
        case 'HD': return 4 // Half Day
        case 'S': return 5 // Substitute
        default: return null
      }
    },
    isWeekendOrHoliday(date) {
      if (!this.serverdata || !this.serverdata.headers) return false
      const dayInfo = this.serverdata.headers.find(h => h.date === date)
      return dayInfo && (dayInfo.day === 'Sun' || dayInfo.is_holiday)
    },
    getCellClass(date) {
      const classes = []
      if (this.isWeekendOrHoliday(date)) {
        classes.push('bg-light')
      }
      if (date === this.selectedDate) {
        classes.push('neo-bg-light-blue')
      }
      return classes.join(' ')
    },
    getAttendanceColor(staffId, attnd) {
      if (attnd.date === this.selectedDate) {
        if (this.presentStaffIds.includes(staffId)) return 'green'
        if (this.absentStaffIds.includes(staffId)) return 'red'
        if (this.lateStaffIds.includes(staffId)) return 'orange'
        if (this.leaveStaffIds.includes(staffId)) return 'purple'
        if (this.halfDayStaffIds.includes(staffId)) return 'cyan'
        if (this.substituteStaffIds.includes(staffId)) return 'magenta'
        return 'white'
      }

      // Historical colors based on backend strings
      if (['P', 'HP', 'LO', 'OT', 'EL', 'LE'].includes(attnd.status)) return 'green'
      if (attnd.status === 'AB') return 'red'
      if (attnd.status === 'LT') return 'orange'
      if (attnd.status === 'L') return 'purple'
      if (attnd.status === 'HD') return 'cyan'
      if (attnd.status === 'S') return 'magenta'

      return 'white'
    },
    initializeAttendanceState() {
      this.presentStaffIds = []
      this.absentStaffIds = []
      this.lateStaffIds = []
      this.leaveStaffIds = []
      this.halfDayStaffIds = []
      this.substituteStaffIds = []
      this.infoVisible = true

      if (!this.serverdata || !this.serverdata.attendancedata) {
        return
      }

      this.serverdata.attendancedata.forEach(staff => {
        const record = staff.data.find(d => d.date === this.selectedDate)
        if (!record) return

        if (['P', 'HP', 'LO', 'OT', 'EL', 'LE'].includes(record.status)) {
          this.presentStaffIds.push(staff.id)
        } else if (record.status === 'AB') {
          this.absentStaffIds.push(staff.id)
        } else if (record.status === 'LT') {
          this.lateStaffIds.push(staff.id)
        } else if (record.status === 'L') {
          this.leaveStaffIds.push(staff.id)
        } else if (record.status === 'HD') {
          this.halfDayStaffIds.push(staff.id)
        } else if (record.status === 'S') {
          this.substituteStaffIds.push(staff.id)
        }

        if (record.status !== '-') {
          this.infoVisible = false
        }
      })
    },

    handleAttendanceClick(staffId, attendanceData) {
      if (attendanceData.date !== this.selectedDate) return

      const dayInfo = this.serverdata.headers.find(h => h.date === this.selectedDate)
      if (dayInfo && (dayInfo.day === 'Sun' || dayInfo.is_holiday)) return

      const isCurrentlyPresent = this.presentStaffIds.includes(staffId)
      const isCurrentlyAbsent = this.absentStaffIds.includes(staffId)
      const isCurrentlyLate = this.lateStaffIds.includes(staffId)
      const isCurrentlyLeave = this.leaveStaffIds.includes(staffId)
      const isCurrentlyHalfDay = this.halfDayStaffIds.includes(staffId)
      const isCurrentlySubstitute = this.substituteStaffIds.includes(staffId)

      // Clear all state for this staff member
      this.presentStaffIds = this.presentStaffIds.filter(id => id !== staffId)
      this.absentStaffIds = this.absentStaffIds.filter(id => id !== staffId)
      this.lateStaffIds = this.lateStaffIds.filter(id => id !== staffId)
      this.leaveStaffIds = this.leaveStaffIds.filter(id => id !== staffId)
      this.halfDayStaffIds = this.halfDayStaffIds.filter(id => id !== staffId)
      this.substituteStaffIds = this.substituteStaffIds.filter(id => id !== staffId)

      // Cycle logic: Present -> Absent -> Late(LT) -> Leave(L) -> Half Day(HD) -> Substitute(S) -> Pending
      if (isCurrentlyPresent) {
        this.absentStaffIds.push(staffId)
      } else if (isCurrentlyAbsent) {
        this.lateStaffIds.push(staffId)
      } else if (isCurrentlyLate) {
        this.leaveStaffIds.push(staffId)
      } else if (isCurrentlyLeave) {
        this.halfDayStaffIds.push(staffId)
      } else if (isCurrentlyHalfDay) {
        this.substituteStaffIds.push(staffId)
      } else if (isCurrentlySubstitute) {
        // Leave pending (empty)
      } else {
        // Was pending, make present
        this.presentStaffIds.push(staffId)
      }
    },

    openEditModal(staffdata) {
      this.selectedStaff = staffdata

      const attendanceRecord = staffdata.data.find(d => d.date === this.selectedDate)

      let status = '-'
      let startTime = null
      let endTime = null

      if (this.presentStaffIds.includes(staffdata.id)) {
        status = 'P'
      } else if (this.absentStaffIds.includes(staffdata.id)) {
        status = 'AB'
      } else if (this.lateStaffIds.includes(staffdata.id)) {
        status = 'LT'
      } else if (this.leaveStaffIds.includes(staffdata.id)) {
        status = 'L'
      } else if (this.halfDayStaffIds.includes(staffdata.id)) {
        status = 'HD'
      } else if (this.substituteStaffIds.includes(staffdata.id)) {
        status = 'S'
      } else if (attendanceRecord) {
        status = attendanceRecord.status
        if (status === '-') {
          status = 'AB'
        }
      }

      if (attendanceRecord) {
        startTime = attendanceRecord.start_time ? moment(attendanceRecord.start_time, 'HH:mm') : null
        endTime = attendanceRecord.end_time ? moment(attendanceRecord.end_time, 'HH:mm') : null
      }

      this.editForm = {
        status,
        startTime,
        endTime,
      }

      this.editModalVisible = true
    },
    closeEditModal() {
      this.editModalVisible = false
    },
    handleEditSubmit() {
      if (!this.selectedStaff) return

      const attendanceRecord = this.selectedStaff.data.find(d => d.date === this.selectedDate)
      const attendanceId = attendanceRecord ? attendanceRecord.id : null

      const needsTime = ['P', 'LT', 'HD'].includes(this.editForm.status)

      const payload = {
        staff_id: this.selectedStaff.id,
        date: this.selectedDate,
        status: this.getStatusCode(this.editForm.status),
        start_time: needsTime && this.editForm.startTime ? this.editForm.startTime.format('HH:mm') : null,
        end_time: needsTime && this.editForm.endTime ? this.editForm.endTime.format('HH:mm') : null,
      }

      if (attendanceId) {
        payload.attendance_id = attendanceId
      }

      this.editLoading = true

      this.$http.post('/api/staff/attendance/single', payload)
        .then(() => {
          this.$message.success('Attendance updated successfully!')
          this.editModalVisible = false
          this.editLoading = false
          this.$emit('attendance-submitted-successfully')
        })
        .catch(error => {
          this.$message.error(
            (error.response && error.response.data && error.response.data.message) || 'Update failed'
          )
          this.editLoading = false
        })
    },
    resetAll() {
      if (!this.serverdata || !this.serverdata.attendancedata) return

      const dayInfo = this.serverdata.headers.find(h => h.date === this.selectedDate)
      if (dayInfo && (dayInfo.day === 'Sun' || dayInfo.is_holiday)) return

      // Only reset the filtered (visible) staff
      const filteredIds = this.filteredStaff.map(s => s.id)
      this.presentStaffIds = this.presentStaffIds.filter(id => !filteredIds.includes(id))
      this.absentStaffIds = this.absentStaffIds.filter(id => !filteredIds.includes(id))
      this.lateStaffIds = this.lateStaffIds.filter(id => !filteredIds.includes(id))
      this.leaveStaffIds = this.leaveStaffIds.filter(id => !filteredIds.includes(id))
      this.halfDayStaffIds = this.halfDayStaffIds.filter(id => !filteredIds.includes(id))
      this.substituteStaffIds = this.substituteStaffIds.filter(id => !filteredIds.includes(id))
    },

    markAllPresent() {
      if (!this.serverdata || !this.serverdata.attendancedata) return

      const dayInfo = this.serverdata.headers.find(h => h.date === this.selectedDate)
      if (dayInfo && (dayInfo.day === 'Sun' || dayInfo.is_holiday)) return

      this.filteredStaff.forEach(staff => {
        const staffId = staff.id
        this.absentStaffIds = this.absentStaffIds.filter(id => id !== staffId)
        this.lateStaffIds = this.lateStaffIds.filter(id => id !== staffId)
        this.leaveStaffIds = this.leaveStaffIds.filter(id => id !== staffId)
        this.halfDayStaffIds = this.halfDayStaffIds.filter(id => id !== staffId)
        this.substituteStaffIds = this.substituteStaffIds.filter(id => id !== staffId)

        if (!this.presentStaffIds.includes(staffId)) {
          this.presentStaffIds.push(staffId)
        }
      })
    },
    markAllAbsent() {
      if (!this.serverdata || !this.serverdata.attendancedata) return

      const dayInfo = this.serverdata.headers.find(h => h.date === this.selectedDate)
      if (dayInfo && (dayInfo.day === 'Sun' || dayInfo.is_holiday)) return

      this.filteredStaff.forEach(staff => {
        const staffId = staff.id
        this.presentStaffIds = this.presentStaffIds.filter(id => id !== staffId)
        this.lateStaffIds = this.lateStaffIds.filter(id => id !== staffId)
        this.leaveStaffIds = this.leaveStaffIds.filter(id => id !== staffId)
        this.halfDayStaffIds = this.halfDayStaffIds.filter(id => id !== staffId)
        this.substituteStaffIds = this.substituteStaffIds.filter(id => id !== staffId)

        if (!this.absentStaffIds.includes(staffId)) {
          this.absentStaffIds.push(staffId)
        }
      })
    },
    handleSubmitAttendance() {
      if (!this.serverdata || !this.serverdata.attendancedata) return

      const dayInfo = this.serverdata.headers.find(h => h.date === this.selectedDate)
      if (dayInfo && (dayInfo.day === 'Sun' || dayInfo.is_holiday)) {
        this.$message.warning('Cannot submit attendance for weekends/holidays')
        return
      }

      this.loading = true

      const staffAttendances = this.serverdata.attendancedata.map(staff => {
        let status = null

        if (this.presentStaffIds.includes(staff.id)) {
          status = 'P'
        } else if (this.absentStaffIds.includes(staff.id)) {
          status = 'AB'
        } else if (this.lateStaffIds.includes(staff.id)) {
          status = 'LT'
        } else if (this.leaveStaffIds.includes(staff.id)) {
          status = 'L'
        } else if (this.halfDayStaffIds.includes(staff.id)) {
          status = 'HD'
        } else if (this.substituteStaffIds.includes(staff.id)) {
          status = 'S'
        }

        // If status is null (blank state), assign -1. Otherwise, get the normal status code.
        const statusCode = status ? this.getStatusCode(status) : -1

        return {
          staff_id: staff.id,
          date: this.selectedDate,
          status: statusCode,
        }
      })

      const payload = {
        staff_attendances: staffAttendances,
      }

      this.$http.post('/api/staff/attendance/manual/entry', payload)
        .then(() => {
          this.$message.success('Attendance submitted successfully!')
          this.$emit('attendance-submitted-successfully')
          this.infoVisible = false
        })
        .catch(error => {
          this.$message.error(
            (error.response && error.response.data && error.response.data.message) || 'Submission failed'
          )
        })
        .finally(() => {
          this.loading = false
        })
    },
  },
}
</script>

<style scoped>
/* Keeping your existing styles */
.staff-attendance-table {
  font-size: 0.8rem;
  overflow-x: visible;
  -webkit-overflow-scrolling: touch;
}

.attendance-compact-table {
  table-layout: auto;
  white-space: nowrap;
}

.staff-attendance-table th,
.staff-attendance-table td {
  padding: 2px 3px;
  vertical-align: middle;
  text-align: center;
}

.staff-attendance-table th {
  background: #ffffff;
  color: #666666;
  font-weight: bold;
}
.staff-attendance-table tbody th:first-child {
  background: white !important;
}

/* Sticky columns for horizontal scroll */
/* Sticky columns removed */

/* Date column headers - compact */
.date-col-header {
  padding: 2px 1px !important;
  min-width: 26px;
  max-width: 30px;
  font-size: 0.7rem;
}

.staff-name-cell {
  min-width: 180px;
  width: 100%;
  text-align: left !important;
}

.cumulative-col-header {
  min-width: 80px;
  width: 80px;
  font-size: 0.72rem;
  padding: 4px 2px !important;
  text-align: center;
  white-space: nowrap;
  vertical-align: middle;
  background-color: #fafafa;
}

.cumulative-col-cell {
  min-width: 80px;
  width: 80px;
  text-align: center;
  vertical-align: middle !important;
  padding: 2px 4px !important;
  white-space: nowrap;
  background-color: #fafafa;
}

.staff-attendance-table tbody td {
  color: #444;
  height: 30px;
}

.bg-light {
  background-color: #f8f9fa !important;
}

.neo-bg-light-blue {
  background-color: #e0f2ff !important;
}

.neoMarkingCell {
  min-width: 26px;
  height: 30px;
  cursor: pointer;
}

.neoMarkingCell:hover:not(.bg-light) {
  background-color: #f0f7ff !important;
}

.neoCellTag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  width: 22px;
  height: 22px;
  border-radius: 4px;
  overflow: hidden;
  line-height: 1;
  box-sizing: border-box;
}

.neoCellTag i {
  font-size: 12px;
  line-height: 1;
}

.neoCellTag small {
  font-size: 9px;
  line-height: 1;
  letter-spacing: -0.5px;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.neoCellTag span {
  font-size: 9px;
  line-height: 1;
  letter-spacing: -0.5px;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.neoCellTagDisabled {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  width: 22px;
  height: 22px;
  border-radius: 4px;
  background: #f5f5f5;
  color: #aaa;
  line-height: 1;
  box-sizing: border-box;
}

.btn-with-addon {
  display: inline-flex;
  align-items: center;
}

.btn-addon {
  display: inline-flex;
  align-items: center;
  padding: 0 5px;
}

.edit-btn {
  padding: 0;
  height: auto;
}

.selected-date-col {
  background-color: #e6f7ff !important;
}
.staff-attendance-table tbody tr:hover td,
.staff-attendance-table tbody tr:hover th {
  background-color: #e6f7ff !important;
  transition: background-color 0.2s ease;
}
.transparent-tag {
  background-color: transparent !important;
}
</style>
<style>
.ant-calendar-date-panel .ant-calendar-selected-date .ant-calendar-date,
.ant-calendar-date-panel .ant-calendar-selected-day .ant-calendar-date {
background-color: blue !important;
color: #ffffff !important;
}
</style>
